import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Award, Loader2, Download, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fireConfetti } from "@/lib/confetti";

export default function IssueCertificateModal({ courses, onClose }) {
  const [courseId, setCourseId] = useState(courses[0]?.id || "");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    if (!courseId) return;
    setIssued(null);
    api.get(`/courses/${courseId}/students`).then((r) => { setStudents(r.data); setStudentId(r.data[0]?.user_id || ""); });
  }, [courseId]);

  const issue = async () => {
    if (!studentId) { toast.error("No student selected"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/certificates/issue`, { student_id: studentId });
      setIssued(data);
      fireConfetti();
      toast.success("Certificate issued!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to issue certificate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="issue-certificate-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Award className="w-4 h-4 text-amber-300" /> Issue Certificate</h3>
          <button onClick={onClose} data-testid="issue-certificate-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {issued ? (
          <div className="text-center py-2" data-testid="issue-certificate-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-white font-semibold">Certificate issued to {issued.student_name}!</p>
            <a href={`${api.defaults.baseURL}/creator/certificates/${issued.certificate_id}/pdf`} data-testid="issue-certificate-download-btn" className="mt-4 inline-flex items-center gap-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 text-amber-300 font-semibold text-sm px-4 py-2.5 rounded-lg">
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        ) : (
          <>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Course</label>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)} data-testid="issue-certificate-course-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-3 block">Student</label>
            {students.length === 0 ? (
              <p className="text-xs text-slate-500 mt-1.5" data-testid="issue-certificate-no-students">No paying students for this course yet.</p>
            ) : (
              <select value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="issue-certificate-student-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                {students.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}{s.certificate_id ? " (already certified)" : ""}</option>)}
              </select>
            )}
            <button onClick={issue} disabled={busy || !studentId} data-testid="issue-certificate-confirm-btn" className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-lg">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} Issue Certificate
            </button>
          </>
        )}
      </div>
    </div>
  );
}
